import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Branding, BrandingCreateRequest, BrandingUpdateRequest } from '@/api/client/models/index.ts';
import { useApiClient } from '@/hooks/api-hook.ts';
import { getDropdownOptions } from '@/modules/dataParser.js';
import { buildAbstractMap } from './common.ts';

export const useBrandingsStore = defineStore('brandings', () => {
  const apiClient = useApiClient();

  const list = ref<Branding[]>([]);
  const abstract = ref<Record<string, string>>({});
  const loading = ref<boolean>(true);

  const options = computed(() => getDropdownOptions(abstract.value));

  async function add(data: Partial<BrandingCreateRequest>): Promise<Branding | undefined> {
    return await apiClient.branding.post(data as BrandingCreateRequest);
  }

  async function toggle({ id, active }: { id: number; active: boolean }): Promise<ArrayBuffer | undefined> {
    return await apiClient.branding.byId(id).byIsActive(active).post();
  }

  async function update(data: Partial<BrandingUpdateRequest>): Promise<ArrayBuffer | undefined> {
    return await apiClient.branding.put(data as BrandingUpdateRequest);
  }

  async function get({ showOnlyActive }: { showOnlyActive?: boolean } = {}): Promise<Branding[]> {
    loading.value = true;

    try {
      const response = await apiClient.branding.get({
        queryParameters: {
          isActive: showOnlyActive,
        },
      });

      list.value = response ?? [];
      abstract.value = buildAbstractMap(list.value, entry => entry.code);
      return list.value;
    } finally {
      loading.value = false;
    }
  }

  return {
    list,
    abstract,
    loading,
    options,
    add,
    toggle,
    update,
    get,
  };
});
